import { IApiError } from '@frontend/request';

import { accountAPIController } from '@aqx-core/common/api/account';
import { EOTPType, IRequestCrmOtpResponse, authenticationAPIController } from '@aqx-core/common/api/auth';
import { ECrmType } from '@aqx-core/common/api/user-account';
import { formatCooldown } from '@aqx-core/common/format/cooldown';
import { i18n } from '@aqx-core/common/i18n';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';

import { signUpCrmService } from './service';
import { ESignUpCrmContent, ICompleteBasicInfoStepParams, ICompleteSignUpParams, IRequestOtpParams } from './types';

// Hardcode crmType & clientPortalUrl to stoneinno
const CRM_TYPE = ECrmType.STONEINNO;

class SignUpCrmViewController {
  public reset = () => {
    signUpCrmService.reset();
  };

  public getCountries = async (onError: (message: string) => void) => {
    signUpCrmService.setLoading(true);
    await accountAPIController
      .getStoneinnoCountries({
        onError: (error) => {
          const { code, message } = error as IApiError;
          onError(mapResponseCodeToMessage(code, message));
        },
        onSuccess: (res) => {
          if (res) {
            signUpCrmService.setCountries(res);
          }
        }
      })
      .finally(() => {
        signUpCrmService.setLoading(false);
      });
  };

  public completeBasicInfoStep = async ({ basicInfo, onError, onSuccess }: ICompleteBasicInfoStepParams) => {
    signUpCrmService.setBasicInfo(basicInfo);
    const onFetchOtpSuccess = (timeoutInSec?: number, expiryTime?: number) => {
      signUpCrmService.setContent(ESignUpCrmContent.VERIFICATION);
      onSuccess(timeoutInSec, expiryTime);
    };

    await this.requestOtp({ onError, onSuccess: onFetchOtpSuccess, isBasicInfoStep: true });
  };

  public requestOtp = async ({ onSuccess, onError, isBasicInfoStep }: IRequestOtpParams) => {
    signUpCrmService.setLoading(true);
    const { email } = signUpCrmService.context.basicInfo || {};
    if (!email) {
      signUpCrmService.setLoading(false);
      return;
    }
    await authenticationAPIController
      .requestCrmOtp(
        { email, crmType: CRM_TYPE, type: EOTPType.EMAIL },
        {
          onSuccess: (result) => {
            const { timeoutInSec, expiryTime } = result || {};
            signUpCrmService.setOtpTimeoutInSec(timeoutInSec);
            signUpCrmService.setOtpExpiryTime(expiryTime);
            onSuccess(timeoutInSec, expiryTime);
          },
          onError: (error) => {
            const { code, message, result } = error as Omit<IApiError, 'result'> & {
              result: IRequestCrmOtpResponse | null;
            };
            // If verfication cool down upon completing basic info step, proceed to navigate user to the next step
            const timeoutInSec = result?.timeoutInSec;
            if (code === ECommonResponseCode.SI_1016 && timeoutInSec) {
              const errorMessage = i18n.t('An OTP was recently sent to you. Please try again after {{duration}}.', {
                duration: formatCooldown(timeoutInSec)
              });
              if (isBasicInfoStep) {
                signUpCrmService.setOtpTimeoutInSec(timeoutInSec);
                signUpCrmService.setOtpExpiryTime(result?.expiryTime);
                onSuccess(timeoutInSec, result?.expiryTime);
              }
              onError(errorMessage, code);
              return;
            }
            onError(mapResponseCodeToMessage(code, message), code);
          }
        }
      )
      .finally(() => {
        signUpCrmService.setLoading(false);
      });
  };

  public completeSignUp = async ({ params, onError, onSuccess }: ICompleteSignUpParams) => {
    const { basicInfo } = signUpCrmService.context;
    if (!basicInfo) {
      return;
    }
    signUpCrmService.setLoading(true);
    const signUpCrmParams = { ...params, ...basicInfo, crmType: CRM_TYPE };
    await authenticationAPIController
      .signUpCrm(signUpCrmParams, {
        onSuccess: (result) => {
          signUpCrmService.setContent(ESignUpCrmContent.COMPLETE);
          if (result) {
            signUpCrmService.setRedirectUrl(result);
          }
          onSuccess && onSuccess(signUpCrmParams);
        },
        onError: (error) => {
          const { code, message } = error as IApiError;
          onError(mapResponseCodeToMessage(code, message));
        }
      })
      .finally(() => {
        signUpCrmService.setLoading(false);
      });
  };
}

export const signUpCrmViewController = new SignUpCrmViewController();
